var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeBreakpointIndexEnum } from '@inter-ikea-kompis/enums';
import { ThemeBreakpointObserver } from '@inter-ikea-kompis/themes';
import { DexfSettingsUtility, PriceUtility, TotalPriceCalculator } from '@inter-ikea-kompis/utilities';
import { PriceFormatter } from '@inter-ikea-kompis/component-price-formatter';
import { PriceModuleLayoutTypeEnum } from '@inter-ikea-kompis/component-price-module';
import { PriceModule } from '@inter-ikea-kompis/component-price-module';
import { ProductRowLayoutEnum } from '@inter-ikea-kompis/component-product-row';
import ProductListModalEnum from '../../enums/ProductListModalEnum.js';
import ProductListTotalPriceStyle from './ProductListTotalPriceStyle.js';
/**
 *
 *
 * @event modalClose Triggered when the modal has been closed
 * @event modalOpen Triggered when a modal has been opened. event.detail.modalType tells you the type
 */
export default class ProductListTotalPrice extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Whether to show the collect checkbox or not.
        this.collectable = false;
        // Show a modal.
        this.visibleModal = null;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Layout for product row. NOTE: ProductRowLayoutEnum.compactPrint should only be used during print.
        this.layout = null;
        this.breakpointObserver = new ThemeBreakpointObserver(() => this.requestUpdate(), this);
        this.dexfSettings = null;
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `${this.getLayout()}`;
    }
    connectedCallback() {
        this.customElements.define('kompis-price-module', PriceModule);
        this.customElements.define('kompis-price-formatter', PriceFormatter);
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.breakpointObserver.disconnect();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('theme')) {
            this.breakpointObserver.observe(this.theme);
        }
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        return true;
    }
    getLayout() {
        if (this.layout === ProductRowLayoutEnum.compactPrint) {
            return this.getCompactPrintLayout();
        }
        else {
            return this.getDefaultLayout();
        }
    }
    getDefaultLayout() {
        const validShoppingProducts = this.shoppingProducts.filter((shoppingProduct) => shoppingProduct.product.valid);
        const totalPrice = TotalPriceCalculator.calculate(validShoppingProducts, this.getSettings().localisation.dateFormat);
        const breakpointIndex = this.breakpointObserver.getBreakpointIndex() ?? ThemeBreakpointIndexEnum.small;
        return html `
			<style>
				${ProductListTotalPriceStyle.getStyle({
            theme: this.theme,
            isTroOrFamilyPrice: totalPrice.temporary || totalPrice.family,
            collectable: this.collectable,
            isMobile: breakpointIndex <= ThemeBreakpointIndexEnum.small,
            isTablet: breakpointIndex <= ThemeBreakpointIndexEnum.xlarge &&
                breakpointIndex >= ThemeBreakpointIndexEnum.small
        })}
			</style>
			<div class="container">
				<div class="spacer"></div>
				<div class="spacerDesktop"></div>
				<div class="total">
					<p class="text--body-m bold">${this.getTranslations().productListTotalPrice}</p>
					${this.getNumberOfItems(validShoppingProducts)}
				</div>
				${this.getNumberOfItems(validShoppingProducts)}
				<div class="priceDisplay">
					<kompis-price-module
						.theme="${this.theme}"
						.localizedInformation="${this.localizedInformation}"
						.translations="${this.translations}"
						.settings="${this.settings}"
						.shoppingProducts="${validShoppingProducts}"
						.showPriceExclTax="${true}"
						.showRegularPriceExclTax="${true}"
						.layout="${PriceModuleLayoutTypeEnum.listPrice}"
						.showEcoFee="${true}"
						.disableLinks="${this.disableLinks}"
						.visibleModal="${this.visibleModal ? this.visibleModal : null}"
						@modalClose="${() => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }))}"
						@modalOpen="${() => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: {
                visibleModal: ProductListModalEnum.ecoFeeCard
            }
        }))}"
					></kompis-price-module>
				</div>
			</div>
		`;
    }
    getCompactPrintLayout() {
        const totalPrice = TotalPriceCalculator.calculate(this.shoppingProducts, this.getSettings().localisation.dateFormat);
        const listTotalPrice = PriceUtility.getPrice(totalPrice);
        return html `<style>
				${ProductListTotalPriceStyle.getCompactLayoutStyle({ theme: this.theme })}
			</style>
			${this.getCompactPriceWrapper({ totalPrice, listTotalPrice })}`;
    }
    getCompactPriceWrapper(options) {
        const { priceInfoIkeaFamilyPrice, priceInfoRegularPrice, productListTotalPriceSummary } = this.getTranslations();
        const { totalPrice, listTotalPrice } = options;
        return totalPrice.family
            ? html `
					<div class="compactPriceWrapper">
						<p class="compactCommercialMessage bold text--body-m">${priceInfoIkeaFamilyPrice}</p>
						<p class="compactPrice bold text--body-m">
							<kompis-price-formatter
								.settings="${this.settings}"
								.localizedInformation="${this.localizedInformation}"
								.amount="${listTotalPrice}"
							></kompis-price-formatter>
						</p>
						<p class="compactCommercialMessage bold text--body-s">${priceInfoRegularPrice}</p>
						<p class="compactPrice bold text--body-s">
							<kompis-price-formatter
								.settings="${this.settings}"
								.localizedInformation="${this.localizedInformation}"
								.amount="${totalPrice.inclTax}"
							></kompis-price-formatter>
						</p>
					</div>
				`
            : html `
					<div class="compactPriceWrapper">
						<p class="compactCommercialMessage bold text--body-m">
							${productListTotalPriceSummary}
						</p>
						<p class="compactPrice bold text--body-m">
							<kompis-price-formatter
								.settings="${this.settings}"
								.localizedInformation="${this.localizedInformation}"
								.amount="${listTotalPrice}"
							></kompis-price-formatter>
						</p>
					</div>
				`;
    }
    getSettings() {
        return this.dexfSettings;
    }
    hasTranslationsAndSettings() {
        return !!this.localizedInformation || !!(this.translations && this.settings);
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    /**
     * Get number of items.
     *
     * @param validShoppingProducts Valid shopping Products.
     * @returns Template.
     */
    getNumberOfItems(validShoppingProducts) {
        return html `
			<div class="numberOfItems">
				<p class="bold text--body-m">
					${validShoppingProducts.reduce((sum, product) => sum + product.quantity, 0)}
				</p>
				<div class="divider"></div>
				<p class="text--body-m">${this.getTranslations().productListItems}</p>
			</div>
		`;
    }
}
__decorate([
    property({ type: Object, required: true })
], ProductListTotalPrice.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], ProductListTotalPrice.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], ProductListTotalPrice.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], ProductListTotalPrice.prototype, "settings", void 0);
__decorate([
    property({ type: Object, required: true })
], ProductListTotalPrice.prototype, "shoppingProducts", void 0);
__decorate([
    property({ type: Boolean })
], ProductListTotalPrice.prototype, "collectable", void 0);
__decorate([
    property({ type: String })
], ProductListTotalPrice.prototype, "visibleModal", void 0);
__decorate([
    property({ type: Boolean })
], ProductListTotalPrice.prototype, "disableLinks", void 0);
__decorate([
    property({ type: String })
], ProductListTotalPrice.prototype, "layout", void 0);
//# sourceMappingURL=ProductListTotalPrice.js.map